import { DateTime } from 'luxon';
import { TransactionType } from './api/transaction-types';
import { useTransactions } from './api/use-get-transactions';
import { useTimeZone } from '../user/api/use-get-user';
import { useMemo } from 'react';
import { useFutureRecurringTransactions } from '../recurring-transaction/recurring-transaction-utils';
import { getDateInfo } from '../utils/date-info-cache';
import { logAndThrowError } from '../utils/errors';
/**
 * Sorts transactions by date in descending order (latest first)
 * and uses ID as a secondary sort criterion in descending order
 *
 * @param transactions - Array of transactions to sort
 * @returns Sorted array of transactions
 */
export function sortTransactionsDescending(transactions) {
    return [...transactions].sort((a, b) => {
        const dateComparison = b.dateEpoch - a.dateEpoch;
        return dateComparison !== 0 ? dateComparison : b.id.localeCompare(a.id);
    });
}
/**
 * Gets non-future transactions from a list of transactions sorted in descending order by date
 *
 * This function efficiently finds non-future transactions by breaking early once
 * a future transaction is encountered, taking advantage of the descending date order.
 * Since the array is sorted descending, all future transactions will be at the beginning.
 *
 * @param transactions - Array of transactions sorted in descending order by date
 * @param timezone - Timezone to use for date comparison
 * @returns Array of non-future transactions (maintains original descending order)
 */
export function getNonFutureTransactions(transactions, timezone) {
    if (!transactions.length) {
        return [];
    }
    const now = DateTime.now().setZone(timezone).startOf('day');
    // Find the first non-future transaction index
    let firstNonFutureIndex = 0;
    for (let i = 0; i < transactions.length; i++) {
        const transactionDate = DateTime.fromSeconds(transactions[i].dateEpoch).setZone(timezone).startOf('day');
        // If transaction is not in the future, we found our starting point
        if (transactionDate <= now) {
            firstNonFutureIndex = i;
            break;
        }
        // If we reach the end without finding a non-future transaction, all are future
        if (i === transactions.length - 1) {
            return [];
        }
    }
    // Return all transactions from the first non-future transaction onwards
    return transactions.slice(firstNonFutureIndex);
}
/**
 * Gets future transactions from a list of transactions sorted in descending order by date
 *
 * This function efficiently finds future transactions by breaking early once
 * a past transaction is encountered, taking advantage of the descending date order.
 * Uses cached date calculations for optimal performance.
 *
 * @param transactions - Array of transactions sorted in descending order by date
 * @param timezone - Timezone to use for date comparison
 * @return Array of future transactions (maintains original descending order)
 */
export function getFutureTransactions(transactions, timezone) {
    if (!transactions.length) {
        return [];
    }
    // Get current day start epoch for comparison (only calculated once)
    const nowEpoch = Date.now();
    const nowDateInfo = getDateInfo(timezone, Math.floor(nowEpoch / 1000));
    const todayStartEpoch = nowDateInfo.dayStartEpoch;
    const futureTransactions = [];
    // Loop through transactions (already sorted in descending order)
    for (const transaction of transactions) {
        // Use cached date info for transaction
        const transactionDateInfo = getDateInfo(timezone, transaction.dateEpoch);
        // If transaction is in the future (day start comparison), add it to our results
        if (transactionDateInfo.dayStartEpoch > todayStartEpoch) {
            futureTransactions.push(transaction);
        }
        else {
            // Since transactions are sorted by date descending,
            // once we find a non-future transaction, we can stop looking
            break;
        }
    }
    return futureTransactions;
}
/**
 * Hook that provides future transactions from a specified year/month
 *
 * @param year - Year to filter transactions for
 * @param month - Month to filter transactions for (1-12)
 * @returns Array of future transactions sorted by date (latest first) and id (newest first)
 */
export function useFutureTransactions(year, month) {
    const transactions = useTransactions();
    const timezone = useTimeZone();
    return useMemo(() => {
        const now = DateTime.now().setZone(timezone);
        const viewMonth = DateTime.fromObject({ year, month }, { zone: timezone });
        // If the viewed month is in the past, return empty array
        if (viewMonth.startOf('month') < now.startOf('month')) {
            return [];
        }
        // filter to only future transactions within the specific month
        const startOfMonth = DateTime.fromObject({ year, month, day: 1 }, { zone: timezone });
        const endOfMonth = startOfMonth.endOf('month');
        const filteredTransactions = transactions.filter((transaction) => {
            const transactionDate = DateTime.fromSeconds(transaction.dateEpoch).setZone(timezone).startOf('day');
            return transactionDate > now && transactionDate >= startOfMonth && transactionDate <= endOfMonth;
        });
        return sortTransactionsDescending(filteredTransactions);
    }, [transactions, year, month, timezone]);
}
/**
 * Hook that provides both future regular transactions and future recurring transactions
 * for the specified month, combined into a single sorted list
 *
 * @param year - Year to calculate transactions for
 * @param month - Month to calculate transactions for (1-12)
 * @returns Array of all future transactions sorted by date (latest first) and id (newest first)
 */
export function useAllFutureTransactions(year, month) {
    const futureRegularTransactions = useFutureTransactions(year, month);
    const futureRecurringTransactions = useFutureRecurringTransactions(year, month);
    // Combine and sort all future transactions
    return useMemo(() => {
        const allFutureTransactions = [...futureRegularTransactions, ...futureRecurringTransactions];
        return sortTransactionsDescending(allFutureTransactions);
    }, [futureRegularTransactions, futureRecurringTransactions]);
}
/**
 * Hook that provides access to all unique payees from transactions.
 *
 * This hook returns a sorted array of all unique payee names that have been
 * used in transactions.
 *
 * @returns An array of unique, sorted payee strings.
 */
export function usePayees() {
    const transactions = useTransactions();
    return useMemo(() => extractUniquePayees(transactions), [transactions]);
}
/**
 * Hook that provides a function to filter payee suggestions based on user input.
 *
 * This hook delegates to the pure `extractUniquePayees` function.
 *
 * @returns A function that takes an input string and returns up to 5 matching payees.
 */
/**
 * Hook that provides a function to filter payee suggestions based on user input.
 *
 * This hook uses the usePayees hook to get all payees and then provides a function
 * to filter them based on user input.
 *
 * @returns A function that takes an input string and returns up to the specified limit of matching payees.
 */
export function usePayeeSuggestions() {
    const payees = usePayees();
    return useMemo(() => {
        return (inputText, limit = 5) => {
            if (!inputText)
                return payees.slice(0, limit);
            const normalizedInput = inputText.toLowerCase();
            const suggestions = [];
            for (const payee of payees) {
                if (payee.toLowerCase().includes(normalizedInput)) {
                    suggestions.push(payee);
                    if (suggestions.length === limit) {
                        break;
                    }
                }
            }
            return suggestions;
        };
    }, [payees]);
}
/**
 * Extracts a distinct, trimmed, and sorted list of payees from a list of transactions.
 *
 * @param transactions - Array of Transaction objects.
 * @returns A sorted array of unique payee strings.
 */
export function extractUniquePayees(transactions) {
    const payeeMap = new Map();
    transactions.forEach((tx) => {
        if (!tx.payee) {
            return;
        }
        const trimmed = tx.payee.trim();
        if (!trimmed) {
            return;
        }
        const normalized = trimmed.toLowerCase();
        if (!payeeMap.has(normalized)) {
            payeeMap.set(normalized, trimmed);
        }
    });
    // Sort payees using case-insensitive comparison.
    return Array.from(payeeMap.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
/**
 * Applies a transaction update to a list of transactions, handling all transfer logic.
 * This mirrors the backend updateTransaction logic.
 */
export function applyTransactionUpdate(transactions, request) {
    // Create a new array to store the updated transactions
    let updatedTransactions = [...transactions];
    // Find the index of the transaction being updated
    const transactionIndex = updatedTransactions.findIndex((t) => t.id === request.id);
    if (transactionIndex === -1) {
        return transactions; // Transaction not found, return original data
    }
    const originalTransaction = updatedTransactions[transactionIndex];
    // **Case 1: Converting Transfer → Regular Transaction **
    if (originalTransaction.type === TransactionType.TRANSFER && request.type === TransactionType.TRANSACTION) {
        const pairedTransactionId = originalTransaction.transferTransactionId;
        // **Step 1: Update the main transaction first**
        updatedTransactions[transactionIndex] = {
            ...request,
            transferTransactionId: undefined,
            transferAccountId: undefined,
        };
        // **Step 2: Now remove the paired transfer transaction (if exists)**
        if (pairedTransactionId) {
            updatedTransactions = updatedTransactions.filter((t) => t.id !== pairedTransactionId);
        }
        updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
        return updatedTransactions;
    }
    // Case 2: Converting from Regular Transaction to Transfer
    if (originalTransaction.type === TransactionType.TRANSACTION && request.type === TransactionType.TRANSFER) {
        if (!request.transferTransactionId || !request.transferAccountId) {
            logAndThrowError('Transfer transaction details required');
        }
        // Update the main transaction
        updatedTransactions[transactionIndex] = {
            ...request,
        };
        // Create new paired transaction
        const transferTransaction = {
            ...request,
            id: request.transferTransactionId,
            accountId: request.transferAccountId,
            transferAccountId: request.accountId,
            transferTransactionId: request.id,
            inflow: request.outflow,
            outflow: request.inflow,
        };
        updatedTransactions.push(transferTransaction);
        updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
        return updatedTransactions;
    }
    // Case 3: Updating existing transfer
    if (request.type === TransactionType.TRANSFER && request.transferTransactionId && request.transferAccountId) {
        // Update the main transaction
        updatedTransactions[transactionIndex] = {
            ...updatedTransactions[transactionIndex],
            ...request,
        };
        // Find and update the paired transaction
        const transferIndex = updatedTransactions.findIndex((t) => t.id === request.transferTransactionId);
        if (transferIndex !== -1) {
            updatedTransactions[transferIndex] = {
                ...updatedTransactions[transferIndex],
                inflow: request.outflow,
                outflow: request.inflow,
                accountId: request.transferAccountId,
                transferAccountId: request.accountId,
                transferTransactionId: request.id,
            };
        }
        updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
        return updatedTransactions;
    }
    // Case 4: Regular transaction update
    updatedTransactions[transactionIndex] = {
        ...updatedTransactions[transactionIndex],
        ...request,
    };
    updatedTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch || b.id.localeCompare(a.id));
    return updatedTransactions;
}
